import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PecaVendidaNoDia, SituacaoReposicao, SugestaoPedido } from '@core/types';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import {
  acrescentarReposicao, atualizarCodigoFabrica, carrinhoParaWhatsApp,
  decidirReposicao, editarItemReposicao, fornecedores,
  gerarPedidoDaReposicao, listarPedidos, pecasVendidasNoDia, pedidoParaWhatsApp,
  reposicaoManualDoDia, responderSugestao, sugerirPedido, sugestoesAbertas,
} from '../lib/consultas';
import { ofereceSalvarNoCadastro } from '../lib/codigoDoCarrinho';
import { textoDasSaidas } from '../lib/saidasDoDia';
import { copiar, textoDoPedidoWhatsApp } from '../lib/pedidoWhatsApp';
import { podeGerenciarCatalogo, useSessao } from '../lib/sessao';
import { negocio } from '@negocio';
import { brl, hojeISO, qtd } from '../lib/formato';
import { dataCurtaDeISO, validadePadrao } from '../lib/orcamento';
import { mensagemDeErro } from '../lib/supabase';

// Aba PEDIDOS — o fechamento do gerente (migração 052).
//
// A rotina que esta tela põe em pé, nas palavras do dono: "antes do almoço e
// no fim do dia, o gerente precisa fazer o fechamento e enviar certos
// pedidos". Antes disso era caderno e memória.
//
// A tela tem três partes, na ordem em que ele trabalha:
//   1. O QUE SAIU HOJE — uma linha por peça (não por venda: decidir a mesma
//      palheta quatro vezes é o que faz o fechamento morrer), com as horas de
//      cada saída. ✕ descarta, ✓ manda para o carrinho do fornecedor.
//   2. OS CARRINHOS — um por fornecedor, com o que foi aprovado. Aqui nasce o
//      pedido de verdade (com data prometida), quando ELE decidir.
//   3. O HISTÓRICO daquele fornecedor — os últimos pedidos, para ele saber o
//      que já pediu antes de pedir de novo.
//
// Não é um mundo paralelo de pedidos: "Gerar pedido" cria um supplier_order
// (040), o mesmo da aba Compras, com número, situação e recebimento que abate
// estoque.

export default function Pedidos() {
  const { papel } = useSessao();
  // O vendedor entra aqui para SUGERIR (053) — o fechamento em si é da
  // gerência. Duas telas na mesma aba, porque para o balcão é o mesmo
  // assunto: "o que precisa ser comprado".
  //
  // A régua é a do CATÁLOGO desde 28/09/2026, e não mais a do caixa: o
  // fechamento é decidir o que comprar (estoque), e o caixa virou só do dono.
  // Sem esta troca, o gerente cairia na tela do vendedor na sua própria
  // rotina de duas vezes por dia.
  if (!podeGerenciarCatalogo(papel)) return <TelaDoVendedor />;
  return <Fechamento />;
}

function Fechamento() {
  const cliente = useQueryClient();
  const [dia, setDia] = useState(hojeISO());
  const [acrescentando, setAcrescentando] = useState(false);

  const lista = useQuery({
    queryKey: ['reposicao', dia],
    queryFn: () => pecasVendidasNoDia(dia),
  });
  const manuais = useQuery({
    queryKey: ['reposicao', 'manual', dia],
    queryFn: () => reposicaoManualDoDia(dia),
  });
  const forn = useQuery({ queryKey: ['fornecedores'], queryFn: () => fornecedores() });
  const pedidos = useQuery({ queryKey: ['pedidos'], queryFn: () => listarPedidos(false) });
  const sugestoes = useQuery({ queryKey: ['sugestoes'], queryFn: () => sugestoesAbertas() });

  const recarregar = () => {
    cliente.invalidateQueries({ queryKey: ['reposicao'] });
    cliente.invalidateQueries({ queryKey: ['pedidos'] });
    cliente.invalidateQueries({ queryKey: ['sugestoes'] });
  };

  const decidir = useMutation({ mutationFn: decidirReposicao, onSuccess: recarregar });
  // O item acrescentado à mão nasce sem fornecedor quando o gerente ainda não
  // sabia de quem pedir; escolher aqui é o mesmo ✓ das peças vendidas.
  const decidirManual = useMutation({
    mutationFn: (p: { item: { chave: string; descricao: string; quantidade: number; product_id: string | null }; fornecedorId: string }) =>
      decidirReposicao({
        dia, chave: p.item.chave, descricao: p.item.descricao, situacao: 'aprovado',
        quantidade: Number(p.item.quantidade), productId: p.item.product_id,
        fornecedorId: p.fornecedorId,
      }),
    onSuccess: recarregar,
  });

  // Os carrinhos: o que está aprovado, agrupado por fornecedor. Deriva da
  // lista + dos manuais — nenhuma segunda verdade.
  const carrinhos = useMemo(() => {
    const mapa = new Map<string, {
      fornecedorId: string; nome: string;
      itens: Array<ItemDoCarrinho>;
    }>();
    const por = (id: string | null, nome: string | null) => {
      if (!id) return null;
      if (!mapa.has(id)) mapa.set(id, { fornecedorId: id, nome: nome ?? 'fornecedor', itens: [] });
      return mapa.get(id)!;
    };
    for (const p of lista.data ?? []) {
      if (p.situacao !== 'aprovado' || p.ja_pedido) continue;
      por(p.fornecedor_id, p.fornecedor_nome)?.itens.push({
        chave: p.chave, descricao: p.descricao, quantidade: Number(p.quantidade),
        productId: p.product_id, codigo: p.codigo_fabricante,
        codigoCadastro: p.codigo_cadastro, codigoBalcao: p.codigo_balcao ?? null,
      });
    }
    for (const m of manuais.data ?? []) {
      if (m.situacao !== 'aprovado' || m.supplier_order_id) continue;
      const nome = (forn.data ?? []).find((f) => f.id === m.fornecedor_id)?.nome ?? null;
      por(m.fornecedor_id, nome)?.itens.push({
        chave: m.chave, descricao: m.descricao, quantidade: Number(m.quantidade),
        // O item manual nunca nasce com peça cadastrada (AcrescentarItem não
        // manda productId), então não há cadastro a oferecer.
        productId: m.product_id, codigo: m.codigo_fabricante, codigoCadastro: null,
        codigoBalcao: null,
      });
    }
    return [...mapa.values()].sort((a, b) => a.nome.localeCompare(b.nome));
  }, [lista.data, manuais.data, forn.data]);

  // O QUE A LISTA MOSTRA (corrigido em 19/08/2026): peça aprovada SAI daqui —
  // ela está no carrinho, à direita. Ficar nos dois lugares foi o defeito que
  // o gerente encontrou no primeiro fechamento de verdade: "só ficou verde e
  // não saiu da lista". A lista é o que FALTA decidir.
  const aDecidir = (lista.data ?? [])
    .filter((p) => !p.ja_pedido && p.situacao === 'pendente');
  // Descartadas e já pedidas não somem para sempre — ficam em blocos
  // recolhidos, porque desfazer um ✕ precisa ser possível.
  const descartadas = (lista.data ?? [])
    .filter((p) => !p.ja_pedido && p.situacao === 'ignorado');
  const jaPedidas = (lista.data ?? []).filter((p) => p.ja_pedido);
  const [verDescartadas, setVerDescartadas] = useState(false);
  const [verJaPedidas, setVerJaPedidas] = useState(false);
  // Acrescentado à mão que ainda espera fornecedor: o que já tem está no
  // carrinho, e repetir aqui seria o mesmo defeito que a lista tinha.
  const semFornecedor = (manuais.data ?? [])
    .filter((m) => m.situacao !== 'aprovado' && !m.supplier_order_id);

  return (
    <Layout
      titulo="Pedidos do dia"
      acao={
        <button type="button" onClick={() => setAcrescentando((v) => !v)}
                className="botao-primario px-3 py-2 text-xs">
          + Item
        </button>
      }
    >
      {/* TRÊS COLUNAS no desktop, na ordem em que o gerente trabalha:
          SUGESTÕES do balcão (esquerda) → o que falta decidir (meio) → os
          CARRINHOS por fornecedor (direita), que é para onde a peça vai
          quando ele aperta ✓. Em telas médias vira duas colunas (os
          carrinhos descem); no celular, uma só, empilhada. */}
      <div className="lg:grid lg:grid-cols-[17rem_minmax(0,1fr)] lg:items-start lg:gap-5
                      xl:grid-cols-[16rem_minmax(0,1fr)_22rem]">
      <div className="mb-4 lg:mb-0">
        <Sugestoes sugestoes={sugestoes.data ?? []} fornecedores={forn.data ?? []}
                   aoResponder={recarregar} />
      </div>

      <div className="space-y-4">
        <section className="cartao flex flex-wrap items-end justify-between gap-3 p-4">
          <div>
            <label className="rotulo" htmlFor="dia-fechamento">Dia do fechamento</label>
            <input id="dia-fechamento" type="date" className="campo" value={dia} max={hojeISO()}
                   onChange={(e) => setDia(e.target.value)} />
          </div>
          <p className="text-sm text-mute">
            {aDecidir.length > 0
              ? <><strong className="text-paper">{aDecidir.length}</strong> peça(s) esperando decisão</>
              : 'Tudo decidido por aqui.'}
          </p>
        </section>

        {acrescentando && (
          <AcrescentarItem dia={dia} aoFechar={() => setAcrescentando(false)}
                           aoSalvar={() => { setAcrescentando(false); recarregar(); }} />
        )}

        {lista.error ? <Aviso tipo="erro">{mensagemDeErro(lista.error)}</Aviso> : null}
        {decidir.error ? <Aviso tipo="erro">{mensagemDeErro(decidir.error)}</Aviso> : null}
        {lista.isLoading && <Carregando texto="Somando o que saiu hoje" />}

        {/* 1. O que saiu hoje e ainda falta decidir */}
        <section className="cartao p-4">
          <h2 className="rotulo">Saiu hoje — a decidir ({aDecidir.length})</h2>
          {(lista.data ?? []).length === 0 && !lista.isLoading && (
            <p className="mt-2 text-sm text-dim">Nenhuma venda neste dia.</p>
          )}
          {aDecidir.length === 0 && (lista.data ?? []).length > 0 && (
            <p className="mt-2 text-sm text-ok">Tudo decidido. ✓</p>
          )}
          <ul className="mt-2 divide-y divide-edge">
            {aDecidir.map((p) => (
              <LinhaPeca key={p.chave} peca={p} dia={dia}
                         fornecedores={forn.data ?? []}
                         ocupado={decidir.isPending}
                         aoDecidir={(situacao, fornecedorId) => decidir.mutate({
                           dia, chave: p.chave, descricao: p.descricao, situacao,
                           quantidade: Number(p.quantidade),
                           productId: p.product_id, fornecedorId,
                         })} />
            ))}
          </ul>

          {/* Descartadas: recolhidas, mas ao alcance — desfazer um ✕ tem que
              ser possível sem virar o dia do avesso. */}
          {descartadas.length > 0 && (
            <div className="mt-3 border-t border-edge pt-2">
              <button type="button" className="text-xs text-dim underline"
                      onClick={() => setVerDescartadas((v) => !v)}>
                {verDescartadas ? 'esconder' : 'ver'} descartadas ({descartadas.length})
              </button>
              {verDescartadas && (
                <ul className="mt-1 divide-y divide-edge">
                  {descartadas.map((p) => (
                    <LinhaPeca key={p.chave} peca={p} dia={dia}
                               fornecedores={forn.data ?? []}
                               ocupado={decidir.isPending}
                               aoDecidir={(situacao, fornecedorId) => decidir.mutate({
                                 dia, chave: p.chave, descricao: p.descricao, situacao,
                                 quantidade: Number(p.quantidade),
                                 productId: p.product_id, fornecedorId,
                               })} />
                  ))}
                </ul>
              )}
            </div>
          )}

          {jaPedidas.length > 0 && (
            <div className="mt-3 border-t border-edge pt-2">
              <button type="button" className="text-xs text-dim underline"
                      onClick={() => setVerJaPedidas((v) => !v)}>
                {verJaPedidas ? 'esconder' : 'ver'} já pedidas ({jaPedidas.length})
              </button>
              {verJaPedidas && (
                <ul className="mt-1 divide-y divide-edge text-sm">
                  {jaPedidas.map((p) => (
                    <li key={p.chave} className="flex justify-between gap-3 py-1.5 opacity-60">
                      <span className="min-w-0 truncate">
                        {p.descricao}
                        {codigoDaLinha(p) && (
                          <span className="ml-2 font-mono text-xs text-signal-soft">{codigoDaLinha(p)}</span>
                        )}
                      </span>
                      <span className="shrink-0 text-xs text-ok">no pedido</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>

        {/* Acrescentados à mão que ainda esperam fornecedor — os que já têm
            estão no carrinho, à direita, e repetir aqui seria o mesmo defeito
            que a lista tinha. */}
        {semFornecedor.length > 0 && (
          <section className="cartao p-4">
            <h2 className="rotulo">Acrescentados à mão — sem fornecedor ({semFornecedor.length})</h2>
            <ul className="mt-2 divide-y divide-edge">
              {semFornecedor.map((m) => (
                <li key={m.id} className="py-2">
                  <p className="text-sm font-semibold">
                    {m.descricao}
                    <span className="ml-2 text-xs font-normal text-dim">
                      {qtd(Number(m.quantidade))} un
                    </span>
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    <select className="campo min-w-0 flex-1 py-1.5 text-xs"
                            aria-label={`Fornecedor de ${m.descricao}`}
                            defaultValue=""
                            onChange={(e) => {
                              if (!e.target.value) return;
                              decidirManual.mutate({ item: m, fornecedorId: e.target.value });
                            }}>
                      <option value="">— escolher fornecedor —</option>
                      {(forn.data ?? []).map((f) => (
                        <option key={f.id} value={f.id}>{f.nome}</option>
                      ))}
                    </select>
                  </div>
                </li>
              ))}
            </ul>
            {decidirManual.error ? (
              <div className="mt-2"><Aviso tipo="erro">{mensagemDeErro(decidirManual.error)}</Aviso></div>
            ) : null}
          </section>
        )}
      </div>

      {/* 2. OS CARRINHOS — a coluna da direita no desktop. É para cá que a
          peça vai quando o gerente aperta ✓, empilhando por fornecedor. */}
      <div className="mt-4 space-y-3 xl:mt-0">
        <h2 className="rotulo">Carrinhos ({carrinhos.length})</h2>
        {carrinhos.length === 0 && (
          <p className="cartao p-4 text-sm text-dim">
            Nenhuma peça aprovada ainda. Toque no <span className="text-ok">✓</span> de
            uma peça (com o fornecedor escolhido) e ela aparece aqui.
          </p>
        )}
        {carrinhos.map((c) => (
          <CarrinhoFornecedor key={c.fornecedorId} carrinho={c} dia={dia}
                              pedidos={(pedidos.data ?? []).filter((p) => p.fornecedor_id === c.fornecedorId)}
                              aoGerar={recarregar} />
        ))}
      </div>
      </div>
    </Layout>
  );
}

/**
 * A coluna das SUGESTÕES: agrupadas POR VENDEDOR, com o motivo de cada uma —
 * exatamente como o dono desenhou. Acatar joga a peça no fechamento de hoje;
 * descartar pede a devolutiva (quem sugeriu merece saber por quê).
 */
function Sugestoes({ sugestoes, fornecedores: fs, aoResponder }: {
  sugestoes: SugestaoPedido[];
  fornecedores: Array<{ id: string; nome: string }>;
  aoResponder: () => void;
}) {
  const porVendedor = useMemo(() => {
    const mapa = new Map<string, SugestaoPedido[]>();
    for (const s of sugestoes) {
      if (!mapa.has(s.vendedor_nome)) mapa.set(s.vendedor_nome, []);
      mapa.get(s.vendedor_nome)!.push(s);
    }
    return [...mapa.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [sugestoes]);

  return (
    <section className="cartao p-4">
      <h2 className="rotulo">Sugestões ({sugestoes.length})</h2>
      {sugestoes.length === 0 && (
        <p className="mt-2 text-xs text-mute">
          Nenhuma sugestão aberta. O balcão sugere pela aba Pedidos dele.
        </p>
      )}
      <div className="mt-2 space-y-4">
        {porVendedor.map(([nome, lista]) => (
          <div key={nome}>
            <p className="text-sm font-bold text-signal-soft">{nome}</p>
            <ul className="mt-1 space-y-2">
              {lista.map((s) => (
                <LinhaSugestao key={s.id} sugestao={s} fornecedores={fs}
                               aoResponder={aoResponder} />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function LinhaSugestao({ sugestao: s, fornecedores: fs, aoResponder }: {
  sugestao: SugestaoPedido;
  fornecedores: Array<{ id: string; nome: string }>;
  aoResponder: () => void;
}) {
  const [fornecedorId, setFornecedorId] = useState('');
  const [descartando, setDescartando] = useState(false);
  const [resposta, setResposta] = useState('');

  const responder = useMutation({
    mutationFn: (acatar: boolean) => responderSugestao({
      id: s.id, acatar,
      resposta: acatar ? null : resposta || null,
      fornecedorId: acatar ? (fornecedorId || null) : null,
    }),
    onSuccess: aoResponder,
  });

  return (
    <li className="border border-edge p-2">
      <p className="text-sm font-semibold leading-snug">
        {s.descricao}
        <span className="ml-2 text-xs font-normal text-dim">{qtd(Number(s.quantidade))} un</span>
      </p>
      {/* O MOTIVO é o que o gerente lê para decidir — por isso em destaque. */}
      <p className="mt-0.5 border-l-2 border-signal/60 pl-2 text-xs text-mute">{s.motivo}</p>

      {descartando ? (
        <div className="mt-2 space-y-2">
          <input className="campo py-1.5 text-xs" value={resposta} autoFocus
                 placeholder="Por quê? (o vendedor vai ler)"
                 onChange={(e) => setResposta(e.target.value)} />
          <div className="flex gap-1">
            <button type="button" className="botao-secundario flex-1 py-1 text-xs"
                    onClick={() => setDescartando(false)}>Voltar</button>
            <button type="button" className="botao-primario flex-1 py-1 text-xs"
                    disabled={responder.isPending}
                    onClick={() => responder.mutate(false)}>Descartar</button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-1">
          <select className="campo min-w-0 flex-1 py-1 text-xs" value={fornecedorId}
                  aria-label={`Fornecedor para ${s.descricao}`}
                  onChange={(e) => setFornecedorId(e.target.value)}>
            <option value="">— fornecedor —</option>
            {fs.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
          <button type="button" aria-label={`Descartar ${s.descricao}`}
                  className="border border-edge px-2 py-1 text-xs font-black text-dim hover:border-erro hover:text-erro"
                  disabled={responder.isPending}
                  onClick={() => setDescartando(true)}>✕</button>
          <button type="button" aria-label={`Acatar ${s.descricao}`}
                  className="border border-edge px-2 py-1 text-xs font-black text-dim hover:border-ok hover:text-ok"
                  disabled={responder.isPending}
                  onClick={() => responder.mutate(true)}>✓</button>
        </div>
      )}
      {responder.error ? (
        <p className="mt-1 text-xs text-erro">{mensagemDeErro(responder.error)}</p>
      ) : null}
    </li>
  );
}

/**
 * A tela do VENDEDOR: sugerir peça para a compra, e ver o que ele já sugeriu.
 * Ele não vê o fechamento (a RLS nem entregaria) — vê a parte que é dele.
 */
function TelaDoVendedor() {
  const cliente = useQueryClient();
  const [descricao, setDescricao] = useState('');
  const [quantidade, setQuantidade] = useState('1');
  const [motivo, setMotivo] = useState('');

  const minhas = useQuery({ queryKey: ['sugestoes'], queryFn: () => sugestoesAbertas() });

  const sugerir = useMutation({
    mutationFn: () => sugerirPedido({
      descricao, motivo, quantidade: Number(quantidade) || 1,
    }),
    onSuccess: () => {
      setDescricao(''); setQuantidade('1'); setMotivo('');
      cliente.invalidateQueries({ queryKey: ['sugestoes'] });
    },
  });

  return (
    <Layout titulo="Sugerir pedido">
      <div className="space-y-4">
        <section className="cartao space-y-3 p-4">
          <p className="text-sm text-mute">
            Viu uma peça acabando, ou um cliente pediu algo que a loja não tem?
            Sugira aqui — o gerente vê no fechamento do dia, com o seu nome e o
            seu motivo.
          </p>
          <div className="grid gap-2 sm:grid-cols-[1fr_5rem]">
            <div>
              <label className="rotulo" htmlFor="sg-desc">Peça</label>
              <input id="sg-desc" className="campo" value={descricao}
                     placeholder="Ex.: Pastilha de freio Onix dianteira"
                     onChange={(e) => setDescricao(e.target.value)} />
            </div>
            <div>
              <label className="rotulo" htmlFor="sg-qtd">Qtd.</label>
              <input id="sg-qtd" className="campo" inputMode="numeric" value={quantidade}
                     onChange={(e) => setQuantidade(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="rotulo" htmlFor="sg-motivo">Motivo (obrigatório)</label>
            <input id="sg-motivo" className="campo" value={motivo}
                   placeholder="Ex.: 3 clientes pediram esta semana e não tínhamos"
                   onChange={(e) => setMotivo(e.target.value)} />
            <p className="mt-1 text-xs text-dim">
              É o motivo que faz o gerente decidir — sem ele a sugestão vira
              lista de desejos.
            </p>
          </div>
          {sugerir.error ? <Aviso tipo="erro">{mensagemDeErro(sugerir.error)}</Aviso> : null}
          <button type="button" className="botao-primario w-full py-3 disabled:opacity-50"
                  disabled={sugerir.isPending || !descricao.trim() || !motivo.trim()}
                  onClick={() => sugerir.mutate()}>
            {sugerir.isPending ? 'Enviando…' : 'Sugerir ao gerente'}
          </button>
        </section>

        <section className="cartao p-4">
          <h2 className="rotulo">Minhas sugestões em aberto ({(minhas.data ?? []).length})</h2>
          {(minhas.data ?? []).length === 0 ? (
            <p className="mt-2 text-sm text-dim">Nenhuma sugestão sua esperando resposta.</p>
          ) : (
            <ul className="mt-2 divide-y divide-edge">
              {(minhas.data ?? []).map((s) => (
                <li key={s.id} className="py-2">
                  <p className="text-sm font-semibold">
                    {s.descricao}
                    <span className="ml-2 text-xs font-normal text-dim">
                      {qtd(Number(s.quantidade))} un
                    </span>
                  </p>
                  <p className="text-xs text-mute">{s.motivo}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Layout>
  );
}

/** O código que a linha mostra: o corrigido no carrinho (063) vence; senão o
 *  digitado no balcão (diversa, 071); senão o do cadastro; senão o SKU. */
function codigoDaLinha(p: PecaVendidaNoDia): string | null {
  return p.codigo_fabricante ?? p.codigo_balcao ?? p.codigo_cadastro ?? p.sku ?? null;
}

/** Uma peça do dia: nome, código, quanto saiu, em que horas e por quem — e a decisão. */
function LinhaPeca({ peca: p, dia, fornecedores: fs, ocupado, aoDecidir }: {
  peca: PecaVendidaNoDia;
  dia: string;
  fornecedores: Array<{ id: string; nome: string }>;
  ocupado: boolean;
  aoDecidir: (situacao: SituacaoReposicao, fornecedorId: string | null) => void;
}) {
  // O fornecedor que a tela vai mandar: o já decidido, ou o sugerido por quem
  // costuma ter a peça. O gerente só mexe quando discorda.
  const [fornecedor, setFornecedor] = useState(
    p.fornecedor_id ?? p.fornecedor_sugerido_id ?? '',
  );

  const aprovado = p.situacao === 'aprovado';
  const ignorado = p.situacao === 'ignorado';

  return (
    <li className={`py-2.5 ${ignorado ? 'opacity-50' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold leading-snug">
            {p.descricao}
            {p.diversa && (
              <span className="ml-2 border border-edge px-1 text-[0.625rem] uppercase text-dim">
                diversa
              </span>
            )}
          </p>
          {/* O CÓDIGO logo abaixo do nome, em vermelho (06/10/2026): é por ele que o
              gerente acha a peça no fornecedor — na diversa, o que o vendedor
              digitou no balcão. */}
          {codigoDaLinha(p) && (
            <p className="font-mono text-sm font-semibold text-signal-soft">{codigoDaLinha(p)}</p>
          )}
          <p className="text-xs text-dim">
            {qtd(Number(p.quantidade))} un · {textoDasSaidas(p.horas, p.vendedores)}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {p.ja_pedido ? (
            <span className="border border-ok/50 px-2 py-1 text-xs font-bold text-ok">
              no pedido
            </span>
          ) : (
            <>
              <button type="button" aria-label={`Não pedir ${p.descricao}`}
                      aria-pressed={ignorado} disabled={ocupado}
                      onClick={() => aoDecidir(ignorado ? 'pendente' : 'ignorado', null)}
                      className={`border px-2.5 py-1.5 text-sm font-black ${
                        ignorado ? 'border-erro bg-erro/15 text-erro'
                                 : 'border-edge text-dim hover:border-erro hover:text-erro'}`}>
                ✕
              </button>
              <button type="button" aria-label={`Pedir ${p.descricao}`}
                      aria-pressed={aprovado} disabled={ocupado || !fornecedor}
                      onClick={() => aoDecidir(aprovado ? 'pendente' : 'aprovado', fornecedor || null)}
                      className={`border px-2.5 py-1.5 text-sm font-black disabled:opacity-40 ${
                        aprovado ? 'border-ok bg-ok/15 text-ok'
                                 : 'border-edge text-dim hover:border-ok hover:text-ok'}`}>
                ✓
              </button>
            </>
          )}
        </div>
      </div>

      {!p.ja_pedido && !ignorado && (
        <div className="mt-1.5 flex items-center gap-2">
          <select className="campo py-1.5 text-xs" value={fornecedor}
                  aria-label={`Fornecedor de ${p.descricao}`}
                  onChange={(e) => {
                    setFornecedor(e.target.value);
                    // Já aprovado? Trocar o fornecedor muda o carrinho na hora.
                    if (aprovado && e.target.value) aoDecidir('aprovado', e.target.value);
                  }}>
            <option value="">— escolher fornecedor —</option>
            {fs.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
          {p.fornecedor_sugerido_nome && !p.fornecedor_id && (
            <span className="shrink-0 text-[0.625rem] text-dim">sugerido</span>
          )}
        </div>
      )}
      {/* O dia fica na linha só para o leitor do código entender a chave. */}
      <span className="hidden" data-dia={dia} />
    </li>
  );
}

/** O painel que corrige o item ANTES de o pedido nascer (063).
 *
 *  Pedido do dono: "preciso editar o nome dos itens na hora de fechar o
 *  pedido, para inserir o código do fabricante numa peça diversa que o
 *  vendedor não inseriu". O carrinho é a última janela em que o texto ainda
 *  não é documento.
 *
 *  O rascunho nasce NO CLIQUE e nunca é reescrito por dado do servidor: esta
 *  tela invalida três consultas a cada ✓ e a cada troca de fornecedor, e um
 *  useEffect de sincronia apagaria o que o dono está digitando (a armadilha
 *  da folha, no CLAUDE.md). */
function EditarItem({ item, dia, aoFechar, aoSalvar }: {
  item: ItemDoCarrinho;
  dia: string;
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  const [nome, setNome] = useState(item.descricao);
  const [codigo, setCodigo] = useState(
    item.codigo ?? item.codigoCadastro ?? item.codigoBalcao ?? '',
  );

  const salvar = useMutation({
    mutationFn: () => editarItemReposicao({
      dia, chave: item.chave, descricao: nome, codigoFabricante: codigo,
    }),
    onSuccess: () => { aoSalvar(); aoFechar(); },
  });
  // A OFERTA é gravação separada, de propósito: salvar no pedido é um clique,
  // salvar no cadastro é outro. Se esta falhar (código que já é de outra
  // peça), o pedido continua corrigido — as duas não se derrubam.
  const noCadastro = useMutation({
    mutationFn: () => atualizarCodigoFabrica(item.productId!, codigo),
  });

  const oferece = ofereceSalvarNoCadastro({
    productId: item.productId,
    codigoNoCadastro: item.codigoCadastro,
    codigoDigitado: codigo,
  });

  return (
    <div className="mt-2 space-y-2 border-t border-edge pt-2">
      <div>
        <label className="rotulo" htmlFor={`nome-${item.chave}`}>Nome no pedido</label>
        <input id={`nome-${item.chave}`} className="campo py-2" value={nome}
               onChange={(e) => setNome(e.target.value)} />
      </div>
      <div>
        <label className="rotulo" htmlFor={`cod-${item.chave}`}>Código do fabricante</label>
        <input id={`cod-${item.chave}`} className="campo py-2 font-mono"
               placeholder="Ex.: SCT-02" value={codigo}
               onChange={(e) => setCodigo(e.target.value)} />
        {!item.productId && (
          <p className="mt-1 text-xs text-dim">
            Peça sem cadastro: o código vai só neste pedido.
          </p>
        )}
      </div>

      {oferece && (
        <div className="border border-alerta/40 bg-alerta/5 p-2">
          <p className="text-xs text-mute">
            Esta peça está no sistema <strong>sem código do fabricante</strong>.
            Salvar no cadastro evita digitar de novo no próximo pedido.
          </p>
          <button type="button" className="botao-terciario mt-1"
                  disabled={noCadastro.isPending || noCadastro.isSuccess}
                  onClick={() => noCadastro.mutate()}>
            {noCadastro.isSuccess ? '✓ salvo no cadastro' : 'Salvar também no cadastro'}
          </button>
          {noCadastro.error ? (
            <p className="mt-1 text-xs text-erro">{mensagemDeErro(noCadastro.error)}</p>
          ) : null}
        </div>
      )}

      {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}
      <div className="flex gap-2">
        <button type="button" className="botao-secundario flex-1 py-1.5 text-xs"
                onClick={aoFechar}>
          Voltar
        </button>
        <button type="button" className="botao-primario flex-1 py-1.5 text-xs"
                disabled={salvar.isPending || nome.trim() === ''}
                onClick={() => salvar.mutate()}>
          {salvar.isPending ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
    </div>
  );
}

/** Uma linha do carrinho. `codigo` é o DIGITADO aqui (063); `codigoCadastro`
 *  é o que está na peça — é a diferença entre os dois que decide se a tela
 *  oferece gravar no cadastro. */
interface ItemDoCarrinho {
  chave: string;
  descricao: string;
  quantidade: number;
  productId: string | null;
  codigo: string | null;
  codigoCadastro: string | null;
  /** O que o vendedor digitou na diversa (071). Desde 06/10/2026 o ✓ já o
   *  copia para `codigo`; este campo só preenche o "editar" das peças
   *  aprovadas antes disso. */
  codigoBalcao: string | null;
}

/** O carrinho de um fornecedor + o histórico dele. */
function CarrinhoFornecedor({ carrinho: c, dia, pedidos, aoGerar }: {
  carrinho: { fornecedorId: string; nome: string; itens: ItemDoCarrinho[] };
  dia: string;
  pedidos: Array<{ id: string; numero: number; data_pedido: string; situacao: string; valor_pedido: number }>;
  aoGerar: () => void;
}) {
  const [previsao, setPrevisao] = useState(() => validadePadrao(hojeISO(), 7));
  const [verHistorico, setVerHistorico] = useState(false);
  // Qual item está com o painel de edição aberto (063). Só a chave — o
  // rascunho do formulário mora no EditarItem, montado no clique.
  const [editando, setEditando] = useState<string | null>(null);

  const gerar = useMutation({
    mutationFn: () => gerarPedidoDaReposicao({
      fornecedorId: c.fornecedorId, dia, previsao,
    }),
    onSuccess: aoGerar,
  });

  // "Copiar para WhatsApp" (18/08/2026): o texto pronto para colar no grupo do
  // representante. O código vem do banco já resolvido (o do fornecedor, senão
  // o do fabricante, senão o SKU) — é o que o representante procura no
  // sistema dele. Copiar falhou? Mostra o texto para selecionar à mão, em vez
  // de fingir que copiou.
  const [textoZap, setTextoZap] = useState<string | null>(null);
  const copiarCarrinho = useMutation({
    mutationFn: async () => {
      const linhas = await carrinhoParaWhatsApp(c.fornecedorId, dia);
      const texto = textoDoPedidoWhatsApp(linhas, {
        loja: negocio.nome, fornecedor: c.nome, data: dataCurtaDeISO(dia),
      });
      const ok = await copiar(texto);
      if (!ok) setTextoZap(texto);
      return ok;
    },
  });
  const copiarPedido = useMutation({
    mutationFn: async (pedidoId: string) => {
      const linhas = await pedidoParaWhatsApp(pedidoId);
      const texto = textoDoPedidoWhatsApp(linhas, {
        loja: negocio.nome, fornecedor: c.nome,
      });
      const ok = await copiar(texto);
      if (!ok) setTextoZap(texto);
      return ok;
    },
  });

  return (
    <article className="cartao p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-bold">{c.nome}</h3>
        <button type="button" className="text-xs text-dim underline"
                onClick={() => setVerHistorico((v) => !v)}>
          {verHistorico ? 'fechar histórico' : `histórico (${pedidos.length})`}
        </button>
      </div>

      <ul className="mt-2 divide-y divide-edge text-sm">
        {c.itens.map((i) => (
          <li key={i.chave} className="py-1.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0">
                <span className="block truncate">{i.descricao}</span>
                {(i.codigo ?? i.codigoCadastro) && (
                  <span className="font-mono text-xs text-signal-soft">
                    {i.codigo ?? i.codigoCadastro}
                  </span>
                )}
              </span>
              <span className="flex shrink-0 items-baseline gap-3">
                <span className="tabular-nums text-dim">{qtd(i.quantidade)} un</span>
                <button type="button" className="text-xs text-dim underline"
                        onClick={() => setEditando(editando === i.chave ? null : i.chave)}>
                  {editando === i.chave ? 'fechar' : 'editar'}
                </button>
              </span>
            </div>
            {editando === i.chave && (
              <EditarItem item={i} dia={dia}
                          aoFechar={() => setEditando(null)}
                          aoSalvar={aoGerar} />
            )}
          </li>
        ))}
      </ul>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div>
          <label className="rotulo" htmlFor={`prev-${c.fornecedorId}`}>Entrega prometida</label>
          <input id={`prev-${c.fornecedorId}`} type="date" className="campo py-2"
                 value={previsao} min={hojeISO()}
                 onChange={(e) => setPrevisao(e.target.value)} />
        </div>
        <button type="button" className="botao-primario flex-1 py-2 text-sm"
                disabled={gerar.isPending || c.itens.length === 0}
                onClick={() => gerar.mutate()}>
          {gerar.isPending ? 'Fechando…' : `Fechar pedido (${c.itens.length} item(ns))`}
        </button>
        <button type="button" className="botao-secundario py-2 text-sm"
                disabled={copiarCarrinho.isPending || c.itens.length === 0}
                onClick={() => copiarCarrinho.mutate()}>
          {copiarCarrinho.isPending ? 'Copiando…'
            : copiarCarrinho.data === true ? 'Copiado ✓' : 'Copiar p/ WhatsApp'}
        </button>
      </div>

      {/* Sem permissão de área de transferência: o texto fica à vista. */}
      {textoZap && (
        <div className="mt-2">
          <p className="text-xs text-alerta">
            O navegador não deixou copiar sozinho — selecione e copie:
          </p>
          <textarea className="campo mt-1 h-32 font-mono text-xs" readOnly value={textoZap} />
        </div>
      )}
      {gerar.error ? (
        <div className="mt-2"><Aviso tipo="erro">{mensagemDeErro(gerar.error)}</Aviso></div>
      ) : null}

      {verHistorico && (
        <div className="mt-3 border-t border-edge pt-2">
          {pedidos.length === 0 ? (
            <p className="text-xs text-dim">Nenhum pedido a este fornecedor ainda.</p>
          ) : (
            <ul className="divide-y divide-edge text-xs">
              {pedidos.slice(0, 8).map((p) => (
                <li key={p.id} className="flex justify-between gap-3 py-1.5">
                  <Link to="/compras" className="text-dim underline">
                    nº {p.numero} · {dataCurtaDeISO(p.data_pedido)}
                  </Link>
                  <span className="flex shrink-0 items-center gap-2">
                    {p.situacao.replace(/_/g, ' ')} · {brl(Number(p.valor_pedido))}
                    <button type="button" className="text-dim underline"
                            disabled={copiarPedido.isPending}
                            onClick={() => copiarPedido.mutate(p.id)}>
                      copiar
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </article>
  );
}

/** O item que não saiu em venda: o gerente viu na prateleira e quer pedir. */
function AcrescentarItem({ dia, aoFechar, aoSalvar }: {
  dia: string; aoFechar: () => void; aoSalvar: () => void;
}) {
  const [descricao, setDescricao] = useState('');
  const [quantidade, setQuantidade] = useState('1');
  const [fornecedorId, setFornecedorId] = useState('');
  const forn = useQuery({ queryKey: ['fornecedores'], queryFn: () => fornecedores() });

  const salvar = useMutation({
    mutationFn: () => acrescentarReposicao({
      descricao, quantidade: Number(quantidade) || 1,
      fornecedorId: fornecedorId || null, dia,
    }),
    onSuccess: aoSalvar,
  });

  return (
    <section className="cartao space-y-3 border-signal/50 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold">Acrescentar item</h2>
        <button type="button" onClick={aoFechar} className="px-2 text-sm text-dim">✕</button>
      </div>
      <p className="text-xs text-mute">
        O que não saiu em venda hoje mas precisa ser pedido — o que você viu
        acabando na prateleira, ou o cliente encomendou.
      </p>
      <div className="grid gap-2 sm:grid-cols-[1fr_5rem_12rem]">
        <div>
          <label className="rotulo" htmlFor="ac-desc">Peça</label>
          <input id="ac-desc" className="campo py-2" value={descricao} autoFocus
                 placeholder="Ex.: Óleo 15W40 (caixa)"
                 onChange={(e) => setDescricao(e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="ac-qtd">Qtd.</label>
          <input id="ac-qtd" className="campo py-2" inputMode="numeric" value={quantidade}
                 onChange={(e) => setQuantidade(e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="ac-forn">Fornecedor</label>
          <select id="ac-forn" className="campo py-2" value={fornecedorId}
                  onChange={(e) => setFornecedorId(e.target.value)}>
            <option value="">— decidir depois —</option>
            {(forn.data ?? []).map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
        </div>
      </div>
      {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}
      <button type="button" className="botao-primario w-full py-2 text-sm disabled:opacity-50"
              disabled={salvar.isPending || !descricao.trim()}
              onClick={() => salvar.mutate()}>
        {salvar.isPending ? 'Salvando…' : 'Acrescentar'}
      </button>
    </section>
  );
}
